/**
 * Which of the group's companies sell this article — one checkbox each.
 *
 * Saved on change, the whole list at once (`PUT`), because the question is
 * "who sells it" and the answer is a set: two operators ticking different
 * boxes a second apart get the last set, never a mixture nobody chose.
 * Inactive companies are listed only when they still hold the article, so
 * the box can be unticked.
 */

import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAction, useResource } from "@hardauto/core-data";
import { Card, Checkbox, useToast } from "@hardauto/core-ui";

import type { ReferentialApi } from "../api";
import { NAMESPACE } from "../i18n";

export interface ArticlePerimeterCardProps {
  api: ReferentialApi;
  articleId: string;
  mayWrite: boolean;
}

export function ArticlePerimeterCard({ api, articleId, mayWrite }: ArticlePerimeterCardProps) {
  const { t } = useTranslation(NAMESPACE);
  const toast = useToast();
  const action = useAction();

  const fetcher = useCallback(async () => {
    const [companies, perimeter] = await Promise.all([
      api.listCompanies(),
      api.articleCompanies(articleId),
    ]);
    return { companies: companies.items, sold: perimeter.company_codes };
  }, [api, articleId]);
  const data = useResource(fetcher);
  // What the screen shows between a click and the server's answer.
  const [pending, setPending] = useState<string[] | null>(null);

  const sold = pending ?? data.data?.sold ?? [];
  const companies = (data.data?.companies ?? []).filter(
    (company) => company.back_office_visible || sold.includes(company.code),
  );

  function toggle(code: string, checked: boolean) {
    const next = checked ? [...sold, code] : sold.filter((value) => value !== code);
    setPending(next);
    void action.run("perimeter", async () => {
      try {
        await api.setArticleCompanies(articleId, next);
        data.reload();
      } catch {
        toast.show(t("error.title"), { tone: "danger" });
      } finally {
        setPending(null);
      }
    });
  }

  return (
    <Card title={t("article.perimeter.title")} compactTitle>
      {companies.length === 0 ? (
        <p className="hardauto-cell__muted">{t("article.perimeter.noCompanies")}</p>
      ) : (
        <div className="hardauto-structure__panel">
          {companies.map((company) => (
            <Checkbox
              key={company.code}
              label={company.name}
              checked={sold.includes(company.code)}
              disabled={!mayWrite || action.isBusy("perimeter")}
              onChange={(event) => toggle(company.code, event.target.checked)}
            />
          ))}
          {sold.length === 0 ? (
            <p className="hardauto-cell__muted">{t("article.perimeter.none")}</p>
          ) : null}
        </div>
      )}
    </Card>
  );
}
